import { useEffect, useMemo, useState, type SyntheticEvent } from 'react'
import {
  PublicFormError,
  submitForm,
  type FormSession,
  type PublicAttendee,
  type PublicFormInfo,
  type SubmissionReceipt,
} from '@/lib/publicForm'
import { createSubmissionSchema, GENDER_CHOICES } from '@/lib/validation/schemas'
import { PickList } from './PickList'

interface Props {
  form: PublicFormInfo
  session: FormSession
  attendees: PublicAttendee[]
  onSubmitted: (receipt: SubmissionReceipt) => void
  /** Called when the form session expired, so the page can re-gate. */
  onSessionExpired: () => void
}

interface Draft {
  matchmakingId: string
  firstName: string
  lastName: string
  email: string
  phone: string
  /** One of GENDER_CHOICES, 'self-describe', '' for unanswered. */
  genderChoice: string
  /** Free text, used only when genderChoice is 'self-describe'. */
  genderSelfDescribed: string
  /** Optional note to the organiser. */
  comment: string
  selected: string[]
}

const EMPTY_DRAFT: Draft = {
  matchmakingId: '',
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  genderChoice: '',
  genderSelfDescribed: '',
  comment: '',
  selected: [],
}

const GENDER_LABELS: Record<string, string> = {
  woman: 'Woman',
  man: 'Man',
  'non-binary': 'Non-binary',
}

/**
 * Drafts survive a reload.
 *
 * Mobile browsers discard and restore background tabs freely, and losing
 * fifteen carefully chosen picks to a reload would be the single worst moment
 * in this product. sessionStorage clears when the tab closes, so nothing
 * lingers on a shared or borrowed phone.
 */
function draftKey(slug: string): string {
  return `sdp.draft.${slug}`
}

function loadDraft(slug: string): Draft {
  try {
    const raw = sessionStorage.getItem(draftKey(slug))
    if (raw === null) return EMPTY_DRAFT
    const parsed = JSON.parse(raw) as Partial<Draft>
    return {
      ...EMPTY_DRAFT,
      ...parsed,
      selected: Array.isArray(parsed.selected) ? parsed.selected : [],
    }
  } catch {
    return EMPTY_DRAFT
  }
}

export function MatchmakingForm({
  form,
  session,
  attendees,
  onSubmitted,
  onSessionExpired,
}: Props) {
  const [draft, setDraft] = useState<Draft>(() => loadDraft(form.slug))
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})
  const [submitting, setSubmitting] = useState(false)

  const selected = useMemo(() => new Set(draft.selected), [draft.selected])

  useEffect(() => {
    try {
      sessionStorage.setItem(draftKey(form.slug), JSON.stringify(draft))
    } catch {
      // A full or disabled storage quota must not break the form.
    }
  }, [draft, form.slug])

  // Once they have typed their own number, take them out of their own list.
  const selfId = useMemo(() => {
    const normalized = draft.matchmakingId.replace(/[^0-9]/g, '').padStart(3, '0')
    if (normalized.length !== 3) return undefined
    return attendees.find((a) => a.matchmaking_id === normalized)?.id
  }, [attendees, draft.matchmakingId])

  // If they picked someone and then typed that person's number as their own,
  // drop the now-invalid self-selection rather than failing at submit.
  useEffect(() => {
    if (selfId !== undefined && selected.has(selfId)) {
      setDraft((current) => ({
        ...current,
        selected: current.selected.filter((id) => id !== selfId),
      }))
    }
  }, [selfId, selected])

  function update<K extends keyof Draft>(key: K, value: Draft[K]) {
    setDraft((current) => ({ ...current, [key]: value }))
  }

  function toggle(attendeeId: string) {
    setDraft((current) => {
      const next = current.selected.includes(attendeeId)
        ? current.selected.filter((id) => id !== attendeeId)
        : [...current.selected, attendeeId]
      return { ...current, selected: next.slice(0, form.max_selections) }
    })
  }

  async function handleSubmit(event: SyntheticEvent<HTMLFormElement>) {
    event.preventDefault()
    setError(null)
    setFieldErrors({})

    const gender =
      draft.genderChoice === 'self-describe' ? draft.genderSelfDescribed : draft.genderChoice

    const parsed = createSubmissionSchema(form.max_selections).safeParse({
      firstName: draft.firstName,
      lastName: draft.lastName,
      email: draft.email,
      phone: draft.phone,
      gender: gender === '' ? null : gender,
      comment: draft.comment === '' ? null : draft.comment,
      matchmakingId: draft.matchmakingId,
      selections: draft.selected,
    })

    if (!parsed.success) {
      const errors: Record<string, string> = {}
      for (const issue of parsed.error.issues) {
        const field = issue.path[0]
        if (typeof field === 'string' && !(field in errors)) errors[field] = issue.message
      }
      setFieldErrors(errors)
      setError('Please check the highlighted fields.')
      return
    }

    setSubmitting(true)
    try {
      const receipt = await submitForm(session, {
        matchmaking_id: parsed.data.matchmakingId,
        first_name: parsed.data.firstName,
        last_name: parsed.data.lastName,
        email: parsed.data.email,
        phone: parsed.data.phone,
        gender: parsed.data.gender,
        comment: parsed.data.comment,
        selections: parsed.data.selections,
      })

      // The draft has served its purpose; leaving it behind would repopulate
      // the form for whoever opens the link next on a shared phone.
      sessionStorage.removeItem(draftKey(form.slug))
      onSubmitted(receipt)
    } catch (err) {
      if (err instanceof PublicFormError && err.code === 'invalid_session') {
        onSessionExpired()
        return
      }
      setError(
        err instanceof PublicFormError ? err.message : 'Something went wrong. Please try again.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  const remaining = form.max_selections - selected.size

  return (
    <form onSubmit={(e) => void handleSubmit(e)} noValidate className="stack">
      <section className="attendee-card">
        <h2>Your details</h2>

        <div className="field">
          <label className="field-label" htmlFor="matchmakingId">
            Your number
          </label>
          <p className="field-hint" id="mm-hint">
            The 3-digit number on your nametag.
          </p>
          <input
            id="matchmakingId"
            // A numeric keypad, but a text field: type="number" would strip a
            // leading zero and "007" is a valid nametag.
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={3}
            value={draft.matchmakingId}
            onChange={(e) => {
              update('matchmakingId', e.target.value.replace(/[^0-9]/g, ''))
            }}
            autoComplete="off"
            enterKeyHint="next"
            aria-describedby="mm-hint"
            aria-invalid={'matchmakingId' in fieldErrors}
            required
          />
          {fieldErrors.matchmakingId !== undefined && (
            <p className="field-error">{fieldErrors.matchmakingId}</p>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="firstName">
            First name
          </label>
          <input
            id="firstName"
            type="text"
            value={draft.firstName}
            onChange={(e) => {
              update('firstName', e.target.value)
            }}
            autoComplete="given-name"
            autoCapitalize="words"
            enterKeyHint="next"
            aria-invalid={'firstName' in fieldErrors}
            required
          />
          {fieldErrors.firstName !== undefined && (
            <p className="field-error">{fieldErrors.firstName}</p>
          )}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="lastName">
            Last name
          </label>
          <input
            id="lastName"
            type="text"
            value={draft.lastName}
            onChange={(e) => {
              update('lastName', e.target.value)
            }}
            autoComplete="family-name"
            autoCapitalize="words"
            enterKeyHint="next"
          />
        </div>

        <div className="field">
          <label className="field-label" htmlFor="email">
            Email
          </label>
          <p className="field-hint" id="email-hint">
            We will email you if you match with someone.
          </p>
          <input
            id="email"
            type="email"
            value={draft.email}
            onChange={(e) => {
              update('email', e.target.value)
            }}
            autoComplete="email"
            autoCorrect="off"
            autoCapitalize="none"
            spellCheck={false}
            enterKeyHint="next"
            aria-describedby="email-hint"
            aria-invalid={'email' in fieldErrors}
            required
          />
          {fieldErrors.email !== undefined && <p className="field-error">{fieldErrors.email}</p>}
        </div>

        <div className="field">
          <label className="field-label" htmlFor="phone">
            Phone
          </label>
          <p className="field-hint" id="phone-hint">
            This is what we give your matches so they can get in touch, so we do need it.
          </p>
          <input
            id="phone"
            type="tel"
            value={draft.phone}
            onChange={(e) => {
              update('phone', e.target.value)
            }}
            autoComplete="tel"
            enterKeyHint="done"
            aria-describedby="phone-hint"
            aria-invalid={'phone' in fieldErrors}
            required
          />
          {fieldErrors.phone !== undefined && <p className="field-error">{fieldErrors.phone}</p>}
        </div>

        {form.collect_gender && (
          <div className="field">
            <label className="field-label" htmlFor="gender">
              Gender
            </label>
            <p className="field-hint" id="gender-hint">
              Optional. It is only used for the organisers&rsquo; own statistics, never shown to
              anyone you match with.
            </p>
            <select
              id="gender"
              value={draft.genderChoice}
              onChange={(e) => {
                update('genderChoice', e.target.value)
              }}
              aria-describedby="gender-hint"
            >
              {/* The empty value is the default, and reads as the honest
                  description of an unanswered question. */}
              <option value="">Prefer not to say</option>
              {GENDER_CHOICES.map((choice) => (
                <option key={choice} value={choice}>
                  {GENDER_LABELS[choice] ?? choice}
                </option>
              ))}
              <option value="self-describe">Prefer to self-describe</option>
            </select>

            {draft.genderChoice === 'self-describe' && (
              <input
                type="text"
                value={draft.genderSelfDescribed}
                onChange={(e) => {
                  update('genderSelfDescribed', e.target.value)
                }}
                aria-label="Describe your gender"
                placeholder="How would you describe it?"
                maxLength={50}
                autoComplete="off"
              />
            )}

            {fieldErrors.gender !== undefined && (
              <p className="field-error">{fieldErrors.gender}</p>
            )}
          </div>
        )}
      </section>

      <section className="attendee-card">
        <div>
          <h2>Who would you like to see again?</h2>
          <p className="field-hint">
            Pick up to {form.max_selections}. You will only be introduced if they pick you too.
          </p>
        </div>

        <PickList
          attendees={attendees}
          selected={selected}
          maxSelections={form.max_selections}
          onToggle={toggle}
          selfId={selfId}
        />
      </section>

      {/* After the list, not before it: the main reason to write anything here
          is a number that turned out not to be on the list, which is something
          you only discover by looking. */}
      <section className="attendee-card">
        <div className="field">
          <label className="field-label" htmlFor="comment">
            Anything else? (optional)
          </label>
          <p className="field-hint" id="comment-hint">
            If you wrote down a number that is not in the list above, put it here. Questions for the
            organisers are welcome too. Only they see this — nobody you match with does.
          </p>
          <textarea
            id="comment"
            value={draft.comment}
            onChange={(e) => {
              update('comment', e.target.value)
            }}
            rows={3}
            maxLength={1000}
            aria-describedby="comment-hint"
            aria-invalid={'comment' in fieldErrors}
          />
          {fieldErrors.comment !== undefined && (
            <p className="field-error">{fieldErrors.comment}</p>
          )}
        </div>
      </section>

      <div className="attendee-sticky">
        {error !== null && (
          <p className="attendee-banner" role="alert">
            {error}
          </p>
        )}
        <p className="attendee-counter" aria-live="polite">
          {selected.size} of {form.max_selections} selected
          {remaining > 0 && selected.size > 0 ? ` · ${String(remaining)} left` : ''}
          {/* Without this, hitting the cap just makes rows stop responding. */}
          {remaining === 0 ? ' · tap a name to swap someone out' : ''}
        </p>
        <button className="attendee-btn" type="submit" disabled={submitting}>
          {submitting ? 'Sending…' : 'Send my picks'}
        </button>
      </div>
    </form>
  )
}
